import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { abs } from "@/lib/site";
import { CopyComboGrid } from "@/components/CopyComboGrid";
import { LENNY_FACES, LENNY_META } from "@/data/lenny-faces";

const TITLE = "Lenny Face — copy and paste ( ͡° ͜ʖ ͡°) text emoticons";

export const Route = createFileRoute("/lenny-faces")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: LENNY_META.tagline },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: LENNY_META.tagline },
      { property: "og:url", content: abs("/lenny-faces") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: abs("/lenny-faces") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Lenny Face",
          description: LENNY_META.tagline,
          url: "/lenny-faces",
          numberOfItems: LENNY_FACES.length,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: abs("/") },
            { "@type": "ListItem", position: 2, name: "Lenny Face", item: abs("/lenny-faces") },
          ],
        }),
      },
    ],
  }),
  component: LennyFacesPage,
});

function LennyFacesPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <span className="text-foreground">Lenny Face</span>
          </nav>

          <h1 className="font-display text-3xl font-bold sm:text-5xl">Lenny Face</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">{LENNY_META.tagline}</p>

          <div className="mt-5 max-w-3xl space-y-4">
            {LENNY_META.intro.map((p, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-foreground/90">{p}</p>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6">
          <h2 className="mb-4 font-display text-xl font-semibold">
            Add your text to any Lenny Face ({LENNY_FACES.length})
          </h2>
          <CopyComboGrid combos={LENNY_FACES} source="lenny-face" layout="face" wrapText wrapNoun="Lenny Face" />
        </section>

        <section className="mx-auto w-full max-w-3xl px-4 pb-16 sm:px-6">
          <h2 className="mb-3 font-display text-xl font-semibold">What is a Lenny Face?</h2>
          <p className="text-[15px] leading-relaxed text-foreground/90">
            A Lenny Face is a text emoticon — ( ͡° ͜ʖ ͡°) — assembled from ordinary characters and a
            few combining marks: a double inverted breve for the raised eyebrow, a double breve below
            for the smile, and the letter lezh (ʖ) for the mouth. Because it is built entirely from
            Unicode text rather than an image, it travels through any text field and keeps working
            where image-based emoji or stickers may not.
          </p>
          <h2 className="mb-3 mt-8 font-display text-xl font-semibold">How to use them</h2>
          <p className="text-[15px] leading-relaxed text-foreground/90">
            Click a face above to copy the whole string, then paste it wherever you are typing.
            Swap the eyes for stars, hearts, money signs or shades to change the mood, or pick one of
            the action faces that flips, shrugs, peeks or holds something. They render best in normal
            text; very stylised fonts may shift the eyebrow slightly, which is part of their charm.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
